/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import {FC, lazy, Suspense} from 'react'
import {BrowserRouter, Navigate, Route, Routes} from 'react-router-dom'
import TopBarProgress from 'react-topbar-progress-indicator'
import {getCSSVariableValue} from '../../_gori/assets/ts/_utils'
import {DisableSidebar} from '../../_gori/layout/core'
import {MasterLayoutForNoAuth} from '../../_gori/layout/MasterLayoutForNoAuth'
import {App} from '../App'
import {AuthPage, Logout, useAuth} from '../modules/auth'
import {MaintenancePage} from '../modules/errors'
import {ErrorsPage} from '../modules/errors/ErrorsPage'
import GlobalWrapper from '../pages/global/GlobalWrapper'
import {LandingPage} from '../pages/landing/LandingPage'
import {ProductPage} from '../pages/landing/ProductPage'
import {BaeGuardPage} from '../pages/landing/BaeGuardPage'
import {GoriSignaturePage} from '../pages/landing/GoriSignaturePage'
import {PricingPage} from '../pages/landing/PricingPage'
import {PrivacyPolicyPage} from '../pages/landing/PrivacyPolicyPage'
import {ServiceTermsPage} from '../pages/landing/ServiceTermsPage'
import OnboardWrapper from '../pages/onboard/OnboardWrapper'
import {PrivateRoutes} from './PrivateRoutes'
import {RetoolLogin} from '../pages/login/RetoolLogin'
import {AmazonIntegration} from '../pages/integrations/AmazonIntegration'
import {ShipStationIntegration} from '../pages/integrations/ShipStationIntegration'
import {ShopifyIntegration} from '../pages/integrations/ShopifyIntegration'
import {WalmartIntegration} from '../pages/integrations/WalmartIntegration'
import {EBayIntegration} from '../pages/integrations/EBayIntegration'
import {TikTokIntegration} from '../pages/integrations/TikTokIntegration'
import {IntegrationsPage} from '../pages/integrations/IntegrationsPage'
import {ROUTES} from '../../_gori/constants'
import {TemuIntegration} from '../pages/integrations/TemuIntegration'
import {WooCommerceIntegration} from '../pages/integrations/WooCommerceIntegration'
import {BlogLayout} from '../pages/blog/BlogLayout'
import {BlogPage} from '../pages/blog/BlogPage'
import {BlogDetailPage} from '../pages/blog/BlogDetailPage'
import {BlogErrorBoundary} from '../pages/blog/BlogErrorBoundary'
import {ReleasesLayout} from '../pages/releases/ReleasesLayout'
import {ReleasesPage} from '../pages/releases/ReleasesPage'
import {BaeSurvey} from '../modules/surveys/BaeSurvey'
import {CILFLeadForm} from '../modules/surveys/CILFLeadForm'
// import {MarketplacePromotion} from '../pages/promotions/MarketplacePromotion'
// import {TemuReferral} from '../pages/promotions/TemuReferral'
import {DemoPage, DemoIntroVideoPage, DemoTopicsPage, DemoPlayerPage} from '../pages/demo'
import {
  GoriSignatureManualWrapper,
  GoriSignatureVerifyWrapper,
  GoriSignatureMethodSelectWrapper,
  GoriSignatureTakePhotoWrapper,
  GoriSignatureUploadPhotoWrapper,
  GoriSignatureScanWrapper,
} from '../pages/gori-signature'
import {AcceptTermsUPic} from '../modules/opt/components/AcceptTermsUPic'
import {ActivateBaeGuardInsurance} from '../modules/opt/components/ActivateBaeGuardInsurance'
import {AgreementPage} from '../pages/landing/AgreementPage'
import {TradeShow} from '../modules/auth/components/TradeShow'
import KMat26Form from '../modules/surveys/KMat26Form'

/**
 * Base URL of the website.
 */
const {PUBLIC_URL} = process.env

const AppRoutes: FC = () => {
  const {currentUser} = useAuth()
  const SupportWrapper = lazy(() => import('../pages/support/SupportWrapper'))
  const HelpCenterPageWrapper = lazy(() => import('../pages/help-center/HelpCenterWrapper'))
  const ContactUsPageWrapper = lazy(() => import('../pages/contact-us/ContactUsWrapper'))

  return (
    <BrowserRouter basename={PUBLIC_URL}>
      <Routes>
        <Route element={<App />}>
          <>
            {<Route path={ROUTES.LANDING.BASE} element={<LandingPage />} />}
            <Route path={ROUTES.PRICING.RELATIVE.BASE} element={<PricingPage />} />
            <Route path={ROUTES.PRODUCTS.PRODUCT} element={<ProductPage />} />
            <Route path={ROUTES.PRODUCTS.INSURANCE} element={<BaeGuardPage />} />
            <Route path={ROUTES.PRODUCTS.GORI_SIGNATURE_PAGE} element={<GoriSignaturePage />} />
            <>
              <Route path={ROUTES.INTEGRATIONS.BASE} element={<IntegrationsPage />} />
              <Route path={ROUTES.INTEGRATIONS.AMAZON} element={<AmazonIntegration />} />
              <Route path={ROUTES.INTEGRATIONS.SHIPSTATION} element={<ShipStationIntegration />} />
              <Route path={ROUTES.INTEGRATIONS.SHOPIFY} element={<ShopifyIntegration />} />
              <Route path={ROUTES.INTEGRATIONS.WALMART} element={<WalmartIntegration />} />
              <Route path={ROUTES.INTEGRATIONS.EBAY} element={<EBayIntegration />} />
              <Route path={ROUTES.INTEGRATIONS.TIKTOK} element={<TikTokIntegration />} />
              <Route path={ROUTES.INTEGRATIONS.TEMU} element={<TemuIntegration />} />
              <Route path={ROUTES.INTEGRATIONS.WOOCOMMERCE} element={<WooCommerceIntegration />} />
              <Route
                path={ROUTES.RESOURCE.BLOG}
                element={
                  <BlogErrorBoundary>
                    <BlogLayout />
                  </BlogErrorBoundary>
                }
              >
                <Route index element={<BlogPage />} />
                <Route path=':slug' element={<BlogDetailPage />} />
              </Route>
              <Route
                path={ROUTES.RESOURCE.RELEASES}
                element={
                  <BlogErrorBoundary>
                    <ReleasesLayout />
                  </BlogErrorBoundary>
                }
              >
                <Route index element={<ReleasesPage />} />
              </Route>
            </>
            <>
              {/* <Route path={ROUTES.PROMOTIONS.MARKETPLACE} element={<MarketplacePromotion />} /> */}
              {/* <Route path={ROUTES.PROMOTIONS.TEMU} element={<TemuReferral />} /> */}
            </>
            <Route path={ROUTES.SERVICE_TERMS.RELATIVE.BASE} element={<ServiceTermsPage />} />
            <Route path={ROUTES.PRIVACY_POLICY.RELATIVE.BASE} element={<PrivacyPolicyPage />} />
            <Route path={ROUTES.AGREEMENT_PAGE.RELATIVE.BASE} element={<AgreementPage />} />
            <Route path={ROUTES.HELP_CENTER.RELATIVE.BASE} element={<HelpCenterPageWrapper />} />
            <Route path={ROUTES.CONTACT_US.RELATIVE.BASE} element={<ContactUsPageWrapper />} />
            <Route path={ROUTES.SURVEY.BAESURVEY} element={<BaeSurvey />} />
            <Route path={ROUTES.SHOW.CILFLEADFORM} element={<CILFLeadForm />} />
            <Route path={ROUTES.AUTH.TRADE_SHOW_KMAT_26} element={<KMat26Form />} />
            <Route path={ROUTES.SB_DEMO.BASE} element={<DemoPage />} />
            <Route path={ROUTES.SB_DEMO.INTRO_VIDEO} element={<DemoIntroVideoPage />} />
            <Route path={ROUTES.SB_DEMO.TOPICS} element={<DemoTopicsPage />} />
            <Route path={ROUTES.SB_DEMO.DEMO_PLAYER} element={<DemoPlayerPage />} />
            <Route path={ROUTES.GORI_SIGNATURE.BASE} element={<GoriSignatureManualWrapper />} />
            <Route path={ROUTES.GORI_SIGNATURE.SCAN} element={<GoriSignatureScanWrapper />} />
            <Route path={ROUTES.GORI_SIGNATURE.HASH} element={<GoriSignatureVerifyWrapper />} />
            <Route
              path={ROUTES.GORI_SIGNATURE.METHOD_SELECT}
              element={<GoriSignatureMethodSelectWrapper />}
            />
            <Route
              path={ROUTES.GORI_SIGNATURE.TAKE_PHOTO}
              element={<GoriSignatureTakePhotoWrapper />}
            />
            <Route
              path={ROUTES.GORI_SIGNATURE.UPLOAD_PHOTO}
              element={<GoriSignatureUploadPhotoWrapper />}
            />
            <Route path={ROUTES.AUTH.TRADE_SHOW} element={<TradeShow />} />
          </>
          <>
            <Route path={ROUTES.ERROR.RELATIVE.BASE} element={<ErrorsPage />} />
            <Route path={ROUTES.GLOBAL.RELATIVE.BASE} element={<GlobalWrapper />} />
            <Route path={ROUTES.MAINTENANCE.BASE} element={<MaintenancePage />} />
            <Route path={ROUTES.AUTH.RETOOL} element={<RetoolLogin />} />
            <Route path={ROUTES.AUTH.LOGOUT} element={<Logout />} />
            <Route path={ROUTES.ACCEPTANCE.TERMS_U_PIC} element={<AcceptTermsUPic />} />
            <Route
              path={ROUTES.ACCEPTANCE.COVERAGE_TERMS_U_PIC}
              element={<ActivateBaeGuardInsurance />}
            />
            {currentUser ? (
              <>
                {currentUser.onboard_step !== 'done' && (
                  <>
                    <Route path={ROUTES.AUTH.ONBOARDING} element={<OnboardWrapper />} />
                    <Route path='*' element={<Navigate to={ROUTES.AUTH.ONBOARDING} />} />
                  </>
                )}
                {currentUser.onboard_step === 'done' && (
                  <Route path='/*' element={<PrivateRoutes />} />
                )}
              </>
            ) : (
              <>
                <Route path={ROUTES.AUTH.RELATIVE.BASE} element={<AuthPage />} />
                <Route element={<MasterLayoutForNoAuth />}>
                  <Route
                    path={ROUTES.SUPPORT.RELATIVE.BASE}
                    element={
                      <SuspenseView>
                        <SupportWrapper />
                      </SuspenseView>
                    }
                  />
                  <Route path='*' element={<Navigate to={ROUTES.LANDING.BASE} />} />
                </Route>
              </>
            )}
          </>
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

const SuspenseView: FC = ({children}) => {
  const baseColor = getCSSVariableValue('--bs-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return (
    <Suspense fallback={<TopBarProgress />}>
      <DisableSidebar>{children}</DisableSidebar>
    </Suspense>
  )
}

export {AppRoutes, SuspenseView}
